import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { CardRecord, SpeakingMeta, SpeakingContent, updateCard } from '../db/indexedDb';
import { CapabilitySnapshot, detectCapability } from './speaking/capability';
import { SpeakingFeedback, feedbackFromGeminiText, ThinkingHints } from './speaking/feedback';
import { buildDraftPrompt } from './speaking/prompts';
import { callGeminiAudio, callGeminiText } from './speaking/genaiClient';
import { generateSpeakingPhraseCard } from '../speaking/phraseCards';
import { speakingLog } from '../utils/logger';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import './SpeakingRelearnCard.css';


/** 获取浏览器支持的音频 MIME 类型 */
const getSupportedMimeType = (): string => {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/wav',
  ];
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) {
      return type;
    }
  }
  return ''; // 使用浏览器默认
};

const blobToDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const parsePhraseList = (text: string) => {
  const lines = text
    .trim()
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const phrases: string[] = [];

  const addPhrase = (raw: string) => {
    const phrase = raw.replace(/^[-*•]\s*/, '').replace(/\s*[\u2022]?\s*$/, '').trim();
    if (!phrase || phrase.length <= 2 || phrase.length >= 50) return;
    if (!phrases.includes(phrase)) phrases.push(phrase);
  };

  for (const line of lines) {
    if (/^[-*•]\s+/.test(line)) {
      addPhrase(line);
      continue;
    }
    if (/^\d+[\).\s-]+\s+/.test(line)) {
      addPhrase(line.replace(/^\d+[\).\s-]+\s+/, ''));
      continue;
    }
    if (/[，,;]/.test(line)) {
      line
        .split(/[,，;；]/)
        .map((item) => item.trim())
        .filter(Boolean)
        .forEach(addPhrase);
      continue;
    }
    if (/^(?:phrase|短语)[:：]/i.test(line)) {
      addPhrase(line.replace(/^(?:phrase|短语)[:：]\s*/i, ''));
    }
  }

  return phrases;
};

const parseDraftResponse = (text: string) => {
  const normalized = text.trim();
  if (!normalized) return { exampleAnswer: '', phrases: [] as string[] };

  // 先尝试 JSON 解析（模型偶尔会返回 JSON）
  if (normalized.startsWith('{')) {
    try {
      const obj = JSON.parse(normalized);
      const exampleAnswer =
        obj.exampleAnswer ??
        obj.rewrittenAnswer ??
        obj.rewritten ??
        obj.answer ??
        obj.text ??
        '';
      const phrasesFromJson: string[] = Array.isArray(obj.phrases)
        ? obj.phrases
        : Array.isArray(obj.phraseSuggestions)
          ? obj.phraseSuggestions.map((item: any) => item?.phrase).filter(Boolean)
          : [];
      return { exampleAnswer: String(exampleAnswer).trim(), phrases: phrasesFromJson.map((p) => String(p).trim()) };
    } catch {
      // Fall through to line-based parsing
    }
  }

  const lines = text.trim().split('\n').map((line) => line.trim()).filter(Boolean);
  let exampleAnswer = '';
  const phrases: string[] = [];
  let currentSection: 'example' | 'phrases' | '' = '';

  const addPhrase = (raw: string) => {
    const phrase = raw.replace(/^[-*•]\s*/, '').replace(/\s*[\u2022]?\s*$/, '').trim();
    if (!phrase || phrase.length <= 2 || phrase.length >= 50) return;
    if (!phrases.includes(phrase)) phrases.push(phrase);
  };

  const parseInlineList = (value: string) => {
    value
      .split(/[,，;；]/)
      .map((item) => item.trim())
      .filter(Boolean)
      .forEach(addPhrase);
  };

  const isBullet = (line: string) => /^[-*•]\s+/.test(line) || /^\d+[\).\s-]+\s+/.test(line);

  for (const line of lines) {
    const upper = line.toUpperCase();
    if (/^EXAMPLE\b/.test(upper) || upper.includes('EXAMPLE ANSWER') || upper.includes('UPDATED EXAMPLE')) {
      currentSection = 'example';
      const inline = line.split(':').slice(1).join(':').trim();
      if (inline) {
        exampleAnswer += (exampleAnswer ? ' ' : '') + inline;
      }
      continue;
    }
    if (/^PHRASES?\b/.test(upper) || upper.includes('KEY PHRASES')) {
      currentSection = 'phrases';
      const inline = line.split(':').slice(1).join(':').trim();
      if (inline) parseInlineList(inline);
      continue;
    }

    if (currentSection === 'example') {
      if (!isBullet(line) && !/^PHRASES?\b/i.test(line)) {
        exampleAnswer += (exampleAnswer ? ' ' : '') + line;
      }
      continue;
    }

    if (currentSection === 'phrases') {
      const phraseLine =
        line.replace(/^\d+[\).\s-]+\s+/, '').replace(/^[-*•]\s+/, '').trim();
      if (isBullet(line)) {
        addPhrase(phraseLine);
        continue;
      }
      if (/^(?:phrase|短语)[:：]/i.test(line)) {
        addPhrase(line.replace(/^(?:phrase|短语)[:：]\s*/i, '').trim());
        continue;
      }
      if (/[，,;]/.test(line)) {
        parseInlineList(line);
      }
    }
  }

  return { exampleAnswer: exampleAnswer.trim(), phrases };
};

const resolveEnvKey = () => {
  if (typeof import.meta === 'undefined') return undefined;
  // 优先使用 OpenRouter API Key
  return (import.meta as any).env?.VITE_OPENROUTER_API_KEY ?? (import.meta as any).env?.OPENROUTER_API_KEY;
};

const getPhraseLimit = (part?: string) => {
  const normalized = part?.toLowerCase() ?? '';
  if (normalized.includes('part 2')) return 6;
  return 3;
};

const fetchPhraseFallback = async (exampleAnswer: string, part?: string) => {
  if (!exampleAnswer.trim()) return [];
  const limit = getPhraseLimit(part);
  const prompt = `From the example answer below, extract ${limit} meaningful spoken English phrases (2-5 words each).
Rules:
- Each phrase must express a concrete meaning (situation/feeling/action), NOT a generic connector.
- Return ONLY the list, one phrase per line, using "- " bullets. No extra text.

Example answer:
"""${exampleAnswer}"""`;

  const text = await callGeminiText('', prompt);
  return parsePhraseList(text).slice(0, limit);
};

type RecordingSlot = 'draft' | 'final';

type RecorderState = {
  slot: RecordingSlot | null;
  status: 'idle' | 'recording';
  startedAt?: number;
};

type UiMessage = { tone: 'info' | 'warn' | 'error'; text: string };

// 展开的例句内容
interface AngleExpansion {
  angle: string;
  phrases: string[];      // 实用短语
  sentences: string[];    // 完整例句
  loading?: boolean;
}

// 多轮对话历史记录
interface DialogHistoryItem {
  id: string;
  draftText: string;
  draftUrl: string | null;
  finalUrl: string | null;
  thinkingHints: ThinkingHints | null;
  angleExpansions: AngleExpansion[];  // 已展开的例句
  feedback: SpeakingFeedback | null;
  createdAt: number;
}

const buildPrompt = (
  question: string | undefined,
  topic: string | undefined,
  cues: string[],
  draft: string | undefined,
  hints: ThinkingHints | null,
  history?: DialogHistoryItem[],
  angleExpansions?: AngleExpansion[]
) => {
  let contextStr = '';
  
  // 添加已展开的例句作为参考素材
  if (angleExpansions && angleExpansions.length > 0) {
    contextStr += '\n\n=== User has prepared these phrases/sentences ===';
    angleExpansions.forEach((exp) => {
      contextStr += `\n\nTopic angle: "${exp.angle}"`;
      if (exp.phrases.length > 0) {
        contextStr += `\nKey phrases: ${exp.phrases.join(', ')}`;
      }
      if (exp.sentences.length > 0) {
        contextStr += `\nPrepared sentences: ${exp.sentences.join(' | ')}`;
      }
    });
    contextStr += '\n=== End of prepared content ===\n';
  }
  
  if (history && history.length > 0) {
    contextStr += '\n\n=== Full conversation history ===';
    history.forEach((h, idx) => {
      contextStr += `\n\nRound ${idx + 1}:`;
      if (h.draftText) {
        contextStr += `\n- User input: "${h.draftText}"`;
      }
      if (h.thinkingHints) {
        contextStr += `\n- AI guidance: ${h.thinkingHints.summary}`;
      }
      if (h.angleExpansions && h.angleExpansions.length > 0) {
        contextStr += `\n- Prepared phrases: ${h.angleExpansions.flatMap(e => e.phrases).join(', ')}`;
      }
      if (h.feedback) {
        contextStr += `\n- Previous evaluation: ${h.feedback.betterVersion || 'N/A'}`;
      }
    });
    contextStr += '\n=== End of history ===\n';
  }

  // 构建完整的问题描述
  const fullQuestion = [question, ...(cues.length > 0 ? cues : [])].filter(Boolean).join('\n- ');
  
  return `You are an IELTS speaking coach. Topic: "${topic ?? 'general'}".
Full Question: "${fullQuestion}"
User draft idea: "${draft ?? 'N/A'}".
Thinking hints: "${hints?.summary ?? 'N/A'}"; angles: ${(hints?.angles ?? []).join(' | ') || 'N/A'}.
${contextStr}

Listen to the audio and evaluate the user's speaking. Return ONLY valid JSON (no markdown):
{"transcript":"<exact transcription>","fluency":"<评价>","grammar":"<评价>","vocab":"<评价>","betterVersion":"<改进版本>"}
Keep each value concise, Chinese OK.`;
};

interface Props {
  card: CardRecord;
  onRecorded?: () => void; // 正式录音完成时调用
}

function mergeMeta(current: SpeakingMeta | undefined, patch: Partial<SpeakingMeta>): SpeakingMeta {
  return {
    ...(current ?? {}),
    ...patch,
    extraMeta: { ...(current?.extraMeta ?? {}), ...(patch.extraMeta ?? {}) },
  };
}

function useCapabilitySnapshot(): CapabilitySnapshot {
  const [cap, setCap] = useState<CapabilitySnapshot>(() => detectCapability({ geminiKey: resolveEnvKey() }));

  useEffect(() => {
    const refresh = () => setCap(detectCapability({ geminiKey: resolveEnvKey() }));
    window.addEventListener('online', refresh);
    window.addEventListener('offline', refresh);
    refresh();
    return () => {
      window.removeEventListener('online', refresh);
      window.removeEventListener('offline', refresh);
    };
  }, []);

  return cap;
}

function SpeakingRelearnCard({ card, onRecorded }: Props) {
  const envKey = resolveEnvKey();
  const effectiveCapability = useCapabilitySnapshot();

  const lastSpokenCardIdRef = useRef<string | null>(null);
  const [ttsStatus, setTtsStatus] = useState<'idle' | 'speaking'>('idle');

  const baseMeta = card.speakingMeta ?? {};
  const [meta, setMeta] = useState<SpeakingMeta>(baseMeta);
  const [draftText, setDraftText] = useState<string>(baseMeta.prompt ?? '');
  const [finalUrl, setFinalUrl] = useState<string | null>(baseMeta.finalRecordingKey ?? card.audioBlobRef ?? null);
  const [draftUrl, setDraftUrl] = useState<string | null>(baseMeta.draftRecordingKey ?? null);
  const [finalBlob, setFinalBlob] = useState<Blob | null>(null);
  const [draftBlob, setDraftBlob] = useState<Blob | null>(null);
  const [recorderState, setRecorderState] = useState<RecorderState>({ slot: null, status: 'idle' });
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);

  const [thinkingHints, setThinkingHints] = useState<ThinkingHints | null>(null);
  const [feedback, setFeedback] = useState<SpeakingFeedback | null>(null);
  const [thinking, setThinking] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [messages, setMessages] = useState<UiMessage[]>([]);
  
  // 多轮对话历史管理
  const [dialogHistory, setDialogHistory] = useState<DialogHistoryItem[]>([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(0);
  const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);
  const audioPlayRef = useRef<HTMLAudioElement | null>(null);
  const touchStartXRef = useRef<number>(0);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  
  // 兼容历史记录（不再使用 angles 展开功能）
  const angleExpansions: AngleExpansion[] = [];

  const speakingContent = (card.content ?? {}) as SpeakingContent & { cues?: string[]; part?: string };
  const question = speakingContent.question;
  const topic = speakingContent.topic;
  const cues = Array.isArray(speakingContent.cues) ? speakingContent.cues : [];
  const part = speakingContent.part;

  const isPartTwo = useMemo(() => {
    const normalized = part?.toLowerCase() ?? '';
    return normalized.includes('part 2') || normalized.includes('part2') || normalized.includes('part two');
  }, [part]);

  const ttsText = useMemo(() => {
    const lines = [question, ...(cues.length > 0 ? cues : [])].filter(Boolean) as string[];
    return lines.join('. ');
  }, [question, cues]);

  const noteCapability = (tone: UiMessage['tone'], text: string) =>
    setMessages((prev) => [...prev.slice(-2), { tone, text }]);

  useEffect(() => {
    const latest = meta.extraMeta?.latestFeedback as any;
    if (latest) {
      setFeedback({
        transcript: meta.transcript ?? latest.transcript,
        fluency: latest.fluency as string | undefined,
        grammar: latest.grammar as string | undefined,
        vocab: latest.vocab as string | undefined,
        betterVersion: meta.bestAnswerSummary ?? (latest.betterVersion as string | undefined),
        source: (latest.feedbackSource as SpeakingFeedback['source']) || 'local',
      });
    } else if (meta.transcript) {
      setFeedback({ transcript: meta.transcript, betterVersion: meta.bestAnswerSummary, source: 'local' });
    }

    const hints = meta.extraMeta?.thinkingHints as ThinkingHints | undefined;
    if (hints) setThinkingHints(hints);
  }, []);

  const speakQuestion = useCallback((afterSpeak?: () => void) => {
    if (isPartTwo || !ttsText) return false;
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return false;

    try {
      const synth = window.speechSynthesis;
      synth.cancel();
      const utterance = new SpeechSynthesisUtterance(ttsText);
      setTtsStatus('speaking');
      utterance.onend = () => {
        setTtsStatus('idle');
        afterSpeak?.();
      };
      utterance.onerror = () => {
        setTtsStatus('idle');
        afterSpeak?.();
      };
      synth.speak(utterance);
      return true;
    } catch (err) {
      speakingLog.warn('tts failed', err);
      setTtsStatus('idle');
      afterSpeak?.();
      return false;
    }
  }, [isPartTwo, ttsText]);

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (!card.id || isPartTwo || !ttsText) return;
    if (recorderState.status === 'recording') return;
    if (lastSpokenCardIdRef.current === card.id) return;
    lastSpokenCardIdRef.current = card.id;
    speakQuestion();
  }, [card.id, isPartTwo, ttsText, recorderState.status, speakQuestion]);

  const persistMeta = async (patch: Partial<SpeakingMeta>, audioBlobRef?: string | null) => {
    const merged = mergeMeta(meta, patch);
    await updateCard(card.id, { speakingMeta: merged, audioBlobRef: audioBlobRef ?? card.audioBlobRef });
    setMeta(merged);
  };

  const persistFeedback = async (fb: SpeakingFeedback) => {
    const extra = {
      fluency: fb.fluency,
      grammar: fb.grammar,
      vocab: fb.vocab,
      betterVersion: fb.betterVersion,
      feedbackSource: fb.source,
    } as Record<string, unknown>;

    await persistMeta(
      {
        transcript: fb.transcript ?? meta.transcript,
        bestAnswerSummary: fb.betterVersion ?? meta.bestAnswerSummary,
        extraMeta: { ...(meta.extraMeta ?? {}), latestFeedback: extra },
      },
      finalUrl
    );
  };

  const stopRecorder = () => {
    speakingLog.debug('stopRecorder called, recorder:', !!recorderRef.current);
    const recorder = recorderRef.current;
    if (!recorder) return;
    speakingLog.debug('calling recorder.stop()');
    recorder.stop();
  };

  const handleRecordingStop = async (slot: RecordingSlot, blob: Blob) => {
    speakingLog.debug('handleRecordingStop called, slot:', slot, 'blob size:', blob.size);
    const dataUrl = await blobToDataUrl(blob);
    const maybeRevoke = (value?: string | null) => {
      if (value && value.startsWith('blob:')) URL.revokeObjectURL(value);
    };
    if (slot === 'draft') {
      maybeRevoke(draftUrl);
      setDraftUrl(dataUrl);
      setDraftBlob(blob);
      await persistMeta({ draftRecordingKey: dataUrl }, dataUrl);
      // 草稿录音完成后，自动调用音频模型整理想法
      await handleThinkingWithAudio(blob);
    } else {
      speakingLog.debug('final recording completed, starting analysis...');
      maybeRevoke(finalUrl);
      setFinalUrl(dataUrl);
      setFinalBlob(blob);
      await persistMeta({ finalRecordingKey: dataUrl }, dataUrl);
      onRecorded?.(); // 通知父组件正式录音完成
      // 正式录音后，自动评估（直接传入 blob，不依赖 state）
      await handleAnalyzeWithBlob(blob);
    }
  };

  const startRecording = async (slot: RecordingSlot) => {
    if (recorderState.status === 'recording') return;
    if (!navigator.mediaDevices?.getUserMedia) {
      noteCapability('error', '当前浏览器不支持录音');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const mimeType = getSupportedMimeType();
      speakingLog.debug('using mimeType:', mimeType || 'browser default');
      const recorderOptions = mimeType ? { mimeType } : undefined;
      const recorder = new MediaRecorder(stream, recorderOptions);
      const actualMimeType = recorder.mimeType || mimeType || 'audio/webm';
      recorderRef.current = recorder;
      recorder.ondataavailable = (evt) => {
        speakingLog.debug('ondataavailable, size:', evt.data.size);
        if (evt.data.size > 0) chunksRef.current.push(evt.data);
      };
      recorder.onstop = async () => {
        speakingLog.debug('recorder.onstop, chunks:', chunksRef.current.length);
        const blob = new Blob(chunksRef.current, { type: actualMimeType });
        speakingLog.debug('created blob, size:', blob.size, 'type:', actualMimeType);
        stream.getTracks().forEach((t) => t.stop());
        setRecorderState({ slot: null, status: 'idle' });
        await handleRecordingStop(slot, blob);
      };
      recorder.start();
      speakingLog.debug('recorder started, slot:', slot);
      setRecorderState({ slot, status: 'recording', startedAt: Date.now() });
    } catch (err) {
      noteCapability('error', `无法启动录音：${(err as Error).message}`);
    }
  };

  // 保存当前状态到历史
  const saveToHistory = (hints: ThinkingHints | null, fb: SpeakingFeedback | null) => {
    const newItem: DialogHistoryItem = {
      id: `history-${Date.now()}`,
      draftText,
      draftUrl,
      finalUrl,
      thinkingHints: hints,
      angleExpansions: [...angleExpansions],  // 保存当前已展开的例句
      feedback: fb,
      createdAt: Date.now(),
    };
    setDialogHistory((prev) => [newItem, ...prev].slice(0, 7)); // 最多保留7条
    setCurrentHistoryIndex(0);
  };

  // 刷新当前内容（重新评估）
  const handleRefresh = () => {
    const current = dialogHistory[currentHistoryIndex];
    if (!current) return;
    
    // 恢复当前历史的内容
    setDraftText(current.draftText);
    setDraftUrl(current.draftUrl);
    setFinalUrl(current.finalUrl);
    setThinkingHints(current.thinkingHints);
    
    // 如果有正式评估，重新评估
    if (current.feedback) {
      handleAnalyze();
    } else if (current.thinkingHints) {
      // 如果只有草稿，重新整理想法
      handleThinking();
    }
  };

  // 触摸左右滑动切换历史
  const handleTouchStartOnCard = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleTouchEndOnCard = (e: React.TouchEvent) => {
    const touchEndX = e.changedTouches[0].clientX;
    const deltaX = touchStartXRef.current - touchEndX;
    const threshold = 50;

    if (Math.abs(deltaX) > threshold && dialogHistory.length > 1) {
      if (deltaX > 0) {
        // 左滑 - 查看更旧的历史
        if (currentHistoryIndex < dialogHistory.length - 1) {
          setSlideDirection('left');
          setTimeout(() => {
            setCurrentHistoryIndex((prev) => prev + 1);
            setSlideDirection(null);
          }, 150);
        }
      } else {
        // 右滑 - 查看更新的历史
        if (currentHistoryIndex > 0) {
          setSlideDirection('right');
          setTimeout(() => {
            setCurrentHistoryIndex((prev) => prev - 1);
            setSlideDirection(null);
          }, 150);
        }
      }
    }
  };

  // 切换历史时更新显示内容（包括输入框）
  useEffect(() => {
    const current = dialogHistory[currentHistoryIndex];
    if (current) {
      // 只有在非最新记录时才锁定输入框内容
      if (currentHistoryIndex > 0) {
        setDraftText(current.draftText);
      } else {
        // 最新记录：显示当前草稿，允许编辑和清空
        setDraftText(current.draftText);
      }
      setDraftUrl(current.draftUrl);
      setFinalUrl(current.finalUrl);
      setThinkingHints(current.thinkingHints);
      setFeedback(current.feedback);
    }
  }, [currentHistoryIndex, dialogHistory]);

  // 播放音频
  const playAudio = (audioUrl: string, id: string) => {
    if (playingAudioId === id) {
      audioPlayRef.current?.pause();
      setPlayingAudioId(null);
      return;
    }

    if (audioPlayRef.current) {
      audioPlayRef.current.pause();
    }

    const audio = new Audio(audioUrl);
    audioPlayRef.current = audio;
    audio.onended = () => setPlayingAudioId(null);
    audio.play();
    setPlayingAudioId(id);
  };

  // 构建历史上下文（语音和文字草稿共享）
  const buildHistoryContext = () => {
    if (dialogHistory.length === 0) return '';
    
    let contextLines: string[] = [];
    contextLines.push('\n=== Previous conversation history ===');
    dialogHistory.forEach((h, idx) => {
      contextLines.push(`\nRound ${idx + 1}:`);
      if (h.draftText) {
        contextLines.push(`User input: "${h.draftText}"`);
      }
      if (h.thinkingHints) {
        contextLines.push(`AI response: ${h.thinkingHints.exampleAnswer || h.thinkingHints.summary}`);
      }
    });
    contextLines.push('\n=== End of history ===\n');
    return contextLines.join('\n');
  };

  // 草稿录音：发送音频给模型，返回完整示例回答和短语
  const handleThinkingWithAudio = async (blob: Blob) => {
    if (effectiveCapability.level !== 2 || !envKey) {
      noteCapability('error', '需要可用的 Gemini 才能整理想法，请检查网络与 API Key');
      return;
    }
    setThinking(true);
    setMessages([]);
    try {
      speakingLog.debug('starting audio thinking, blob size:', blob.size);

      const historyContext = buildHistoryContext();

      // 获取当前的示例回答（如果有），用于修改模式
      const previousExampleAnswer = thinkingHints?.exampleAnswer;

      // 使用分 Part 的提示词
      const draftPrompt = buildDraftPrompt({
        part,
        question,
        topic,
        cues,
        draft: '(from audio)',
        historyContext,
        previousExampleAnswer,
      });
      
      const text = await callGeminiAudio({
        apiKey: envKey,
        blob,
        prompt: draftPrompt + '\n\nListen to the user\'s draft ideas in the audio.',
        mimeType: blob.type,
      });

      speakingLog.debug('audio thinking result:', text?.slice(0, 200));
      
      // 解析 exampleAnswer 和 phrases（兼容格式偏差）
      const parsed = parseDraftResponse(text);
      let exampleAnswer = parsed.exampleAnswer;
      let phrases = parsed.phrases;
      
      // 如果没有解析到，使用默认值
      if (!exampleAnswer) {
        exampleAnswer = question || '(AI 未能生成示例回答)';
      }
      if (phrases.length === 0) {
        try {
          phrases = await fetchPhraseFallback(exampleAnswer, part);
        } catch (err) {
          speakingLog.warn('fallback phrase extract failed', err);
        }
      }
      // 不再使用通用连接词作为默认值，保持空数组
      
      const hints: ThinkingHints = {
        summary: exampleAnswer.slice(0, 60) + '...',
        exampleAnswer,
        phrases: phrases.slice(0, 6),
        angles: [],
        dense: exampleAnswer.slice(0, 120),
        source: 'gemini',
      };
      setThinkingHints(hints);
      await persistMeta({ extraMeta: { ...(meta.extraMeta ?? {}), thinkingHints: hints } });
      noteCapability('info', 'AI 示例回答已生成');
      
      // 保存到历史（草稿模式）
      saveToHistory(hints, null);
    } catch (err) {
      speakingLog.error('audio thinking failed:', err);
      noteCapability('error', `语音整理想法失败：${(err as Error).message}`);
    } finally {
      setThinking(false);
    }
  };

  const handleThinking = async () => {
    if (effectiveCapability.level !== 2 || !envKey) {
      noteCapability('error', '需要可用的 Gemini 才能整理想法，请检查网络与 API Key');
      return;
    }
    if (!draftText && !question) {
      noteCapability('warn', '请先输入草稿要点或题目内容');
      return;
    }
    setThinking(true);
    setMessages([]);
    try {
      // 使用共享的历史上下文
      const historyContext = buildHistoryContext();

      // 获取当前的示例回答（如果有），用于修改模式
      const previousExampleAnswer = thinkingHints?.exampleAnswer;

      // 使用分 Part 的提示词
      const draftPrompt = buildDraftPrompt({
        part,
        question,
        topic,
        cues,
        draft: draftText,
        historyContext,
        previousExampleAnswer,
      });
      
      const text = await callGeminiText(envKey, draftPrompt);
      
      // 解析 exampleAnswer 和 phrases（兼容格式偏差）
      const parsed = parseDraftResponse(text);
      let exampleAnswer = parsed.exampleAnswer;
      let phrases = parsed.phrases;
      
      // 如果没有解析到示例回答，使用默认值
      if (!exampleAnswer) {
        exampleAnswer = draftText || question || '(AI 未能生成示例回答)';
      }
      if (phrases.length === 0) {
        try {
          phrases = await fetchPhraseFallback(exampleAnswer, part);
        } catch (err) {
          speakingLog.warn('fallback phrase extract failed', err);
        }
      }
      // 不再使用通用连接词作为默认值，保持空数组
      
      const hints: ThinkingHints = {
        summary: exampleAnswer.slice(0, 60) + '...',
        exampleAnswer,
        phrases: phrases.slice(0, 6),
        angles: [],
        dense: exampleAnswer.slice(0, 120),
        source: 'gemini',
      };
      setThinkingHints(hints);
      await persistMeta({ extraMeta: { ...(meta.extraMeta ?? {}), thinkingHints: hints } });
      noteCapability('info', 'AI 示例回答已生成');
      
      // 保存到历史（草稿模式）
      saveToHistory(hints, null);
    } catch (err) {
      noteCapability('error', `生成示例失败：${(err as Error).message}`);
    } finally {
      setThinking(false);
    }
  };

  // 直接传入 blob 的分析函数（录音完成时调用）
  const handleAnalyzeWithBlob = async (blob: Blob) => {
    if (effectiveCapability.level !== 2 || !envKey) {
      noteCapability('error', '需要可用的 Gemini 才能转写与反馈，请检查网络与 API Key');
      return;
    }

    setAnalyzing(true);
    setMessages([]);

    try {
      speakingLog.debug('starting audio analysis, blob size:', blob.size);
      const text = await callGeminiAudio({
        apiKey: envKey,
        blob,
        prompt: buildPrompt(question, topic, cues, draftText, thinkingHints, dialogHistory, angleExpansions),
        mimeType: blob.type,
      });
      speakingLog.debug('audio analysis result:', text?.slice(0, 200));
      const fb = feedbackFromGeminiText(text);
      setFeedback(fb);
      await persistFeedback(fb);
      noteCapability('info', 'Gemini 2.5 Flash 已完成转写');
      
      // 保存到历史（正式评估模式）
      saveToHistory(thinkingHints, fb);
      
      // 生成口语表达提升卡（基于 transcript/betterVersion），失败不阻塞
      generateSpeakingPhraseCard(card, fb.transcript ?? draftText, fb.betterVersion).catch((err) =>
        console.warn('[speaking phrase card] skip', err)
      );
    } catch (err) {
      speakingLog.error('audio analysis failed:', err);
      noteCapability('error', `Gemini 转写/反馈失败：${(err as Error).message}`);
    } finally {
      setAnalyzing(false);
    }
  };

  // 手动触发分析（使用 state 中的 finalBlob）
  const handleAnalyze = async () => {
    if (!finalBlob) {
      noteCapability('warn', '请先录制正式回答（需有本次音频）');
      return;
    }
    await handleAnalyzeWithBlob(finalBlob);
  };

  const currentModeLabel = useMemo(() => {
    const base = effectiveCapability.label;
    if (!effectiveCapability.online) return `${base} · 离线`;
    if (effectiveCapability.level === 0) return `${base} · 录音回放`;
    if (effectiveCapability.level === 1) return `${base} · STT`;
    return `${base} · Gemini`;
  }, [effectiveCapability]);

  // 录音计时 - 每秒更新
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  useEffect(() => {
    if (recorderState.status !== 'recording' || !recorderState.startedAt) {
      setElapsedSeconds(0);
      return;
    }
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - recorderState.startedAt!) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [recorderState.status, recorderState.startedAt]);

  const currentHistory = dialogHistory[currentHistoryIndex];
  const hasHistory = dialogHistory.length > 0;

  // 键盘快捷键处理
  const handleToggleRecording = useCallback(() => {
    if (currentHistoryIndex > 0) return;  // 历史记录模式下不允许录音

    if (recorderState.status === 'recording') {
      // 正在录音 → 停止
      stopRecorder();
    } else {
      // 开始正式录音
      const started = speakQuestion(() => {
        startRecording('final');
      });
      if (!started) startRecording('final');
    }
  }, [recorderState.status, currentHistoryIndex, speakQuestion, startRecording]);

  const handlePlayCurrentAudio = useCallback(() => {
    // 播放当前的正式录音
    if (finalUrl) {
      playAudio(finalUrl, 'final-audio');
    } else if (draftUrl) {
      playAudio(draftUrl, 'draft-audio');
    }
  }, [finalUrl, draftUrl]);

  const handleSubmitDraft = useCallback(() => {
    if (draftText.trim() && !thinking && !analyzing && currentHistoryIndex === 0) {
      handleThinking();
    }
  }, [draftText, thinking, analyzing, currentHistoryIndex]);

  // 全局快捷键
  useKeyboardShortcuts({
    shortcuts: {
      'r': handleToggleRecording,  // R = 开始/停止正式录音
      ' ': handlePlayCurrentAudio,  // Space = 播放录音
      'p': handlePlayCurrentAudio,  // P = 播放录音
    },
  });

  return (
    <div className="practice-card speaking-compact">
      {/* Header: Question only */}
      <div className="practice-card-header">
        <h3 className="practice-question">{question ?? '口语题目'}</h3>
        {cues.length > 0 && (
          <div className="practice-cues">
            {cues.slice(0, 3).map((cue) => (
              <span key={cue} className="pill">{cue}</span>
            ))}
          </div>
        )}
      </div>

      {/* 输入行 - 单行输入 + 横向按钮 */}
      <div className="speaking-input-row">
        <input
          type="text"
          className="speaking-input"
          placeholder="记录想法、关键词或草稿..."
          value={draftText}
          onChange={(e) => setDraftText(e.target.value)}
          disabled={thinking || analyzing || currentHistoryIndex > 0}
        />
        <div className="speaking-actions-row">
          <button 
            type="button" 
            className={`speaking-btn ${recorderState.status === 'recording' && recorderState.slot === 'draft' ? 'recording' : ''}`}
            disabled={currentHistoryIndex > 0 || (recorderState.status === 'recording' && recorderState.slot !== 'draft')}
            onClick={() => {
              if (recorderState.status === 'recording' && recorderState.slot === 'draft') {
                stopRecorder();
              } else if (recorderState.status !== 'recording') {
                startRecording('draft');
              }
            }}
            title="草稿录音（仅保存）"
          >
            {recorderState.status === 'recording' && recorderState.slot === 'draft' ? `${elapsedSeconds}s` : '🎤'}
          </button>
          <button 
            type="button" 
            className="speaking-btn primary"
            onClick={async () => {
              if (draftText.trim()) await handleThinking();
            }}
            disabled={thinking || analyzing || !draftText.trim() || currentHistoryIndex > 0}
          >
            {thinking ? '...' : '→'}
          </button>
          <button 
            type="button" 
            className={`speaking-btn record ${recorderState.status === 'recording' && recorderState.slot === 'final' ? 'recording' : ''}`}
            disabled={currentHistoryIndex > 0}
            onClick={() => {
              if (recorderState.status === 'recording' && recorderState.slot === 'final') {
                stopRecorder();
                if (ttsStatus === 'speaking') {
                  window.speechSynthesis?.cancel();
                }
              } else if (recorderState.status !== 'recording') {
                const started = speakQuestion(() => {
                  startRecording('final');
                });
                if (!started) startRecording('final');
              }
            }}
            title="正式录音并分析"
          >
            {recorderState.status === 'recording' && recorderState.slot === 'final' ? `${elapsedSeconds}s` : '⏺'}
          </button>
        </div>
      </div>

      {/* 反馈卡区域 - 常驻固定高度，支持左右滑动 */}
      <div 
        className="speaking-feedback-carousel"
        onTouchStart={handleTouchStartOnCard}
        onTouchEnd={handleTouchEndOnCard}
      >
        {/* 反馈内容 - 带滑动动画 */}
        <div className={`feedback-content-fixed ${slideDirection ? `slide-${slideDirection}` : ''}`}>
          {/* 加载状态优先显示 */}
          {(thinking || analyzing) ? (
            <div className="feedback-loading">
              <div className="loading-spinner"><span></span></div>
              <p className="loading-text">{analyzing ? '正在分析录音' : '正在整理想法'}</p>
            </div>
          ) : hasHistory && currentHistory ? (
            <>
              {/* 状态栏 */}
              <div className="feedback-status-row">
                <span className={`status-badge ${currentHistory.feedback ? 'evaluated' : currentHistory.thinkingHints ? 'draft' : 'empty'}`}>
                  {currentHistory.feedback ? 'EVALUATED' : currentHistory.thinkingHints ? 'DRAFT' : 'EMPTY'}
                </span>
                <button type="button" className="refresh-btn" onClick={handleRefresh}>↻</button>
              </div>

              {/* 示例回答和短语 */}
              {currentHistory.thinkingHints && (
                <div className="example-answer-section">
                  {/* 完整示例回答 */}
                  {currentHistory.thinkingHints.exampleAnswer && (
                    <div className="example-answer">
                      <span className="label">示例回答:</span>
                      <p>{currentHistory.thinkingHints.exampleAnswer}</p>
                    </div>
                  )}
                  {/* 短语列表 */}
                  {currentHistory.thinkingHints.phrases && currentHistory.thinkingHints.phrases.length > 0 && (
                    <div className="phrases-section">
                      <span className="label">常用短语:</span>
                      <div className="phrases-list">
                        {currentHistory.thinkingHints.phrases.map((phrase, i) => (
                          <span key={i} className="phrase-chip">{phrase}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* 评估结果 */}
              {currentHistory.feedback && (
                <div className="feedback-result">
                  {currentHistory.feedback.transcript && (
                    <div className="feedback-transcript">
                      <span className="label">转写:</span>
                      <p>{currentHistory.feedback.transcript}</p>
                    </div>
                  )}
                  <div className="feedback-scores">
                    {currentHistory.feedback.fluency && <span>流利: {currentHistory.feedback.fluency}</span>}
                    {currentHistory.feedback.vocab && <span>词汇: {currentHistory.feedback.vocab}</span>}
                    {currentHistory.feedback.grammar && <span>语法: {currentHistory.feedback.grammar}</span>}
                  </div>
                  {currentHistory.feedback.betterVersion && (
                    <div className="feedback-better">
                      <span className="label">改进版:</span>
                      <p>{currentHistory.feedback.betterVersion}</p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="feedback-empty">
              <div className="feedback-guide">
                <p className="guide-title">如何使用</p>
                <div className="guide-steps">
                  <div className="guide-step">
                    <span className="guide-icon">🎤</span>
                    <span className="guide-text">录音并转文字，帮你快速记录想法</span>
                  </div>
                  <div className="guide-step">
                    <span className="guide-icon primary">→</span>
                    <span className="guide-text">发送给 AI，获取表达建议和反馈</span>
                  </div>
                  <div className="guide-step">
                    <span className="guide-icon record">⏺</span>
                    <span className="guide-text">正式作答录音，获取反馈和评估</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 底部页码指示器 - 左右滑动切换 */}
        {dialogHistory.length > 1 && (
          <div className="carousel-dots">
            {dialogHistory.map((_, idx) => (
              <span
                key={idx}
                className={`carousel-dot ${idx === currentHistoryIndex ? 'active' : ''}`}
                onClick={() => setCurrentHistoryIndex(idx)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SpeakingRelearnCard;
